import { useState } from "react";
import { Link } from "react-router-dom";
import { BestCarousel } from "../components/BestCarousel";
import { type PostCard, useBestPosts, useCategories, usePosts } from "../lib/api";

function Card({ post }: { post: PostCard }) {
  return (
    <Link
      to={`/projects/${post.id}`}
      className="btn-neon block overflow-hidden rounded-2xl border-cyan-400/40 !text-cyan-100 transition duration-200 hover:-translate-y-0.5 hover:border-cyan-300/70 hover:shadow-[0_0_28px_rgba(34,211,238,0.28)]"
    >
      {post.thumbnailUrl ? (
        <img
          src={post.thumbnailUrl}
          alt={post.title}
          className="aspect-video w-full border-b border-cyan-400/20 object-cover"
        />
      ) : (
        <div className="flex aspect-video items-center justify-center border-b border-cyan-400/20 bg-gradient-to-br from-cyan-500/20 to-fuchsia-500/20 text-3xl">
          <span className="neon-text font-mono">{post.categoryName ?? "·"}</span>
        </div>
      )}
      <div className="p-4">
        {post.categoryName && (
          <span className="rounded bg-cyan-400/15 px-2 py-0.5 font-mono text-[10px] text-cyan-200">
            {post.categoryName}
          </span>
        )}
        <h3 className="neon-text mt-2 line-clamp-2 font-semibold">{post.title}</h3>
        <div className="mt-3 flex items-center justify-between text-xs text-cyan-100/60">
          <span>{post.authorNickname}</span>
          <span className="font-mono">
            ❤ {post.likeCount} · 💬 {post.commentCount}
          </span>
        </div>
      </div>
    </Link>
  );
}

export function Home() {
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const { data: categories } = useCategories();
  const { data: best } = useBestPosts();
  const { data, isLoading } = usePosts({ board: "public", categoryId, page, q: q || undefined });

  const chip = (label: string, active: boolean, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "btn-neon rounded-full px-3 py-1 text-xs font-medium"
          : "rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-400 transition hover:text-slate-200"
      }
    >
      {label}
    </button>
  );

  return (
    <section className="fade-up">
      <BestCarousel posts={best ?? []} />
      <form
        className="mb-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setQ(searchInput.trim());
          setPage(1);
        }}
      >
        <input
          aria-label="프로젝트 검색"
          placeholder="프로젝트 검색"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="input-cyber w-full max-w-xs rounded-lg px-3 py-2 text-sm"
        />
        <button type="submit" className="btn-neon rounded-lg px-4 text-sm font-medium">
          검색
        </button>
      </form>
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {chip("전체", categoryId === undefined, () => {
          setCategoryId(undefined);
          setPage(1);
        })}
        {categories?.map((cat) =>
          chip(cat.name, categoryId === cat.id, () => {
            setCategoryId(cat.id);
            setPage(1);
          }),
        )}
      </div>

      {isLoading ? (
        <p className="mt-16 text-center font-mono text-sm text-cyan-300/70">불러오는 중…</p>
      ) : !data || data.posts.length === 0 ? (
        <p className="mt-16 text-center text-slate-400">
          아직 프로젝트가 없습니다. 첫 프로젝트를 올려보세요!
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {data.posts.map((post) => (
            <Card key={post.id} post={post} />
          ))}
        </div>
      )}

      {data && data.total > data.pageSize && (
        <div className="mt-8 flex justify-center gap-3 text-sm">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="btn-neon rounded-lg px-3 py-1 disabled:opacity-40"
          >
            이전
          </button>
          <span className="py-1 text-slate-400">{page}</span>
          <button
            type="button"
            disabled={page * data.pageSize >= data.total}
            onClick={() => setPage((p) => p + 1)}
            className="btn-neon rounded-lg px-3 py-1 disabled:opacity-40"
          >
            다음
          </button>
        </div>
      )}
    </section>
  );
}
